import React, { useMemo, useState, useEffect } from 'react';
import { AmountDisplay, TruncatedAddress, useAddress } from '@onomy/react-hub';
import useInterval from '@use-it/interval';
import { OnomyHubConstants } from '@onomy/hub';
import { useNavigate } from 'react-router-dom';

import { Caret, Close, EthLogo, Success } from 'components/Modals/Icons';
import { useModal } from 'context/modal/ModalContext';
import * as Modal from 'components/Modals/styles';
import {
  ExplorerButton,
  OptionBtn,
  OptionCaption,
  Options,
  OptionsWrapper,
  TransactionDetailsRow,
  WalletIcon,
} from './exchangemodalStyles';
import { useExchange } from 'context/exchange/ExchangeContext';
import { SymbolDisplay } from '../exchangeStyles';
import { SlippageDetails } from './SlippageDetails';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import TransactionFailedModal from 'components/Modals/components/TransactionFailedModal';

function shorten(addr: string) {
  return `${addr.slice(0, 15)}...${addr.slice(addr.length - 3)}`;
}

export function ConfirmBuyBridgeNomModal() {
  const { handleModal } = useModal();
  const navigate = useNavigate();
  const [ethAdddress] = useAddress(OnomyHubConstants.ETH_ASSET);
  const {
    bNomQuote,
    ethBidAmount,
    buyGas,
    buySlippagePoints,
    setBuySlippagePoints,
    buyTxStatus: { pending: isPending, error: txError, finished: isFinished },
    buyTxDetails,
    doPurchase,
    buyReset,
  } = useExchange();

  const exchangeRate = useMemo(() => {
    const bid = parseFloat(
      buyTxDetails ? buyTxDetails.ethDecrease.amountFormatted : ethBidAmount.amountFormatted
    );
    const ask = parseFloat(
      buyTxDetails ? buyTxDetails.bNomIncrease.amountFormatted : bNomQuote.amountFormatted
    );
    return ask / bid;
  }, [bNomQuote, ethBidAmount, buyTxDetails]);

  const exploreUrl = useMemo(() => {
    if (!buyTxDetails) return '';
    return `https://${
      buyTxDetails.ethBalanceAfter.asset.chain.name === 'ethereum'
        ? ''
        : `${buyTxDetails.ethBalanceAfter.asset.chain.name}.`
    }etherscan.io/tx/${buyTxDetails.ethTx.hash}`;
  }, [buyTxDetails]);

  const [count, setCount] = useState(60);
  const [delay, setDelay] = useState<number | null>(1000);
  const [detailsActive, setDetailsActive] = useState(false);

  const increaseCount = () => {
    if (isPending || txError || isFinished) return;
    if (count === 0) {
      setDelay(null);
      handleModal();
    } else {
      setCount(count - 1);
    }
  };

  useInterval(increaseCount, delay);

  function onConfirm() {
    doPurchase();
  }

  function onCloseAfterFinish() {
    handleModal();
    navigate('/bridge-initial');
  }

  useEffect(() => {
    return () => buyReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (txError) return <TransactionFailedModal error={txError} header="Buy bNOM Failed" />;

  return (
    <Modal.Wrapper>
      {!isPending && (
        <Modal.CloseIcon onClick={() => handleModal()} data-testid="confirm-modal-close-icon">
          <Close />
        </Modal.CloseIcon>
      )}

      {isFinished && buyTxDetails ? (
        <main>
          <Modal.ModalIconWrapper>
            <Success />
          </Modal.ModalIconWrapper>
          <Modal.Caption>Transaction Confirmed</Modal.Caption>
          <a href={exploreUrl} target="_blank" rel="noreferrer">
            <ExplorerButton>View in Etherscan</ExplorerButton>
          </a>
          <Modal.ExchangeResult data-testid="completed-modal-exchange-result">
            + <AmountDisplay balance={buyTxDetails.bNomIncrease} />
            <Modal.Spent>
              - <AmountDisplay balance={buyTxDetails.ethDecrease} />
            </Modal.Spent>
          </Modal.ExchangeResult>
          <Modal.ExchangeRateWrapper data-testid="completed-modal-exchange-rate">
            <span>Exchange Rate</span>

            <strong>
              <strong>1 ETH = {exchangeRate.toFixed(6)} bNOM</strong>
            </strong>
          </Modal.ExchangeRateWrapper>
        </main>
      ) : (
        <main>
          {isPending ? (
            <Modal.PendingCaption>Waiting for Confirmation</Modal.PendingCaption>
          ) : (
            <Modal.Caption>Confirm Transaction</Modal.Caption>
          )}

          <Modal.ExchangeResult>
            <Modal.ExchangeResultDescription>You&apos;re receiving</Modal.ExchangeResultDescription>
            ~ <AmountDisplay balance={bNomQuote} />
          </Modal.ExchangeResult>

          <TransactionDetailsRow>
            <span>Current Exchange Rate</span>
            <strong>1 ETH = {exchangeRate.toFixed(6)} bNOM</strong>
          </TransactionDetailsRow>
          <TransactionDetailsRow>
            <span>You&apos;re sending</span>
            <strong>
              <AmountDisplay balance={ethBidAmount} SymbolComponent={SymbolDisplay} />
            </strong>
          </TransactionDetailsRow>
          <TransactionDetailsRow>
            <div>
              <span>Wallet</span>

              <div>
                <strong>{ethAdddress ? <TruncatedAddress address={ethAdddress} /> : null}</strong>
              </div>
            </div>

            <WalletIcon>
              <EthLogo />
            </WalletIcon>
          </TransactionDetailsRow>
        </main>
      )}

      {!isPending && !isFinished && (
        <OptionsWrapper>
          <OptionCaption>Gas Fee</OptionCaption>
          <Options>
            {buyGas.options.map(opt => (
              <OptionBtn
                type="button"
                active={buyGas.gasPrice === opt.value}
                key={opt.value}
                onClick={() => buyGas.setGasPrice(opt.value)}
              >
                <AmountDisplay balance={opt.amount} SymbolComponent={null} /> ({opt.label})
              </OptionBtn>
            ))}
          </Options>
          <SlippageDetails slippage={buySlippagePoints} setSlippage={setBuySlippagePoints} />
        </OptionsWrapper>
      )}

      {isFinished && buyTxDetails ? (
        <footer>
          <Modal.FooterControls>
            <Modal.DetailsButton
              active={detailsActive}
              onClick={() => setDetailsActive(!detailsActive)}
              data-testid="completed-modal-details-button"
            >
              View Details <Caret />
            </Modal.DetailsButton>
            <Modal.PrimaryButton
              onClick={() => onCloseAfterFinish()}
              data-testid="completed-modal-primary-button"
            >
              Next
            </Modal.PrimaryButton>
          </Modal.FooterControls>

          {detailsActive && (
            <Modal.FooterDetails data-testid="completed-modal-footer-details">
              <Modal.FooterDetailsRow>
                <span>From</span> <strong>{shorten(buyTxDetails.ethTx.from)}</strong>
              </Modal.FooterDetailsRow>
              {!!buyTxDetails.ethTx.to && (
                <Modal.FooterDetailsRow>
                  <span>To</span> <strong>{shorten(buyTxDetails.ethTx.to)}</strong>
                </Modal.FooterDetailsRow>
              )}
              <Modal.FooterDetailsRow>
                <span>TxID</span> <strong>{shorten(buyTxDetails.ethTx.hash)}</strong>
              </Modal.FooterDetailsRow>
              <Modal.FooterDetailsRow>
                <span>Network Confirmations</span>{' '}
                <strong>
                  {buyTxDetails.ethTx.confirmations > 0 ? buyTxDetails.ethTx.confirmations : 1}
                </strong>
              </Modal.FooterDetailsRow>

              <a href={exploreUrl} target="_blank" rel="noreferrer">
                <ExplorerButton data-testid="completed-model-explorer-button">
                  View in Explorer
                </ExplorerButton>
              </a>
            </Modal.FooterDetails>
          )}
        </footer>
      ) : (
        <footer>
          {isPending ? (
            <Modal.LoadingWrapper>
              <LoadingSpinner />
            </Modal.LoadingWrapper>
          ) : (
            <Modal.FooterControls>
              <Modal.SecondaryButton
                onClick={() => handleModal()}
                data-testid="confirm-modal-secondary-button"
              >
                Cancel
              </Modal.SecondaryButton>
              <Modal.PrimaryButton
                onClick={() => onConfirm()}
                data-testid="confirm-modal-primary-button"
              >
                Confirm ({count})
              </Modal.PrimaryButton>
            </Modal.FooterControls>
          )}
        </footer>
      )}
    </Modal.Wrapper>
  );
}
